import React, { useState } from 'react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Revenue } from '../../types'
import Spinner from '../Spinner'

interface RevenueChartProps {
  data: Revenue[]
  isLoading?: boolean
}

const RevenueChart: React.FC<RevenueChartProps> = ({ data, isLoading = false }) => {
  const [chartType, setChartType] = useState<'line' | 'area' | 'bar'>('area')
  const [metric, setMetric] = useState<'total' | 'subscription' | 'tip'>('total')

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-center items-center h-80">
          <Spinner size="lg" />
        </div>
      </div>
    )
  }

  const getDataKey = () => {
    switch (metric) {
      case 'subscription':
        return 'subscription_revenue'
      case 'tip':
        return 'tip_revenue'
      default:
        return 'total_revenue'
    }
  }

  const getMetricLabel = () => {
    switch (metric) {
      case 'subscription':
        return 'Subscription Revenue'
      case 'tip':
        return 'Tip Revenue'
      default:
        return 'Total Revenue'
    }
  }

  const renderChart = () => {
    const dataKey = getDataKey()
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    }

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(value) => `$${value}`} />
            <Legend />
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke="#3b82f6"
              strokeWidth={2}
              name={getMetricLabel()}
            />
          </LineChart>
        )
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(value) => `$${value}`} />
            <Legend />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke="#3b82f6"
              fillOpacity={1}
              fill="url(#colorRevenue)"
              name={getMetricLabel()}
            />
          </AreaChart>
        )
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(value) => `$${value}`} />
            <Legend />
            <Bar dataKey={dataKey} fill="#3b82f6" name={getMetricLabel()} />
          </BarChart>
        )
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h3 className="text-lg font-bold text-gray-900">Revenue Trends</h3>

        <div className="flex gap-2">
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as 'total' | 'subscription' | 'tip')}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="total">Total Revenue</option>
            <option value="subscription">Subscriptions</option>
            <option value="tip">Tips</option>
          </select>

          <div className="inline-flex rounded-md shadow-sm">
            <button
              onClick={() => setChartType('area')}
              className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                chartType === 'area'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Area
            </button>
            <button
              onClick={() => setChartType('line')}
              className={`px-3 py-1.5 text-sm font-medium border-t border-b ${
                chartType === 'line'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Line
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-1.5 text-sm font-medium rounded-r-md border ${
                chartType === 'bar'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Bar
            </button>
          </div>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="flex justify-center items-center h-80 text-gray-500">
          No revenue data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          {renderChart()}
        </ResponsiveContainer>
      )}
    </div>
  )
}

export default RevenueChart
